import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Collections from "@/pages/Collections";
import CollectionBrand from "@/pages/CollectionBrand";
import Cart from "@/pages/Cart";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Bookings from "@/pages/Bookings";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import Account from "@/pages/Account";
import Wishlist from "@/pages/Wishlist";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminContacts from "@/pages/admin/AdminContacts";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminBuildClub from "@/pages/admin/AdminBuildClub";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
    const location = useLocation();
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/shop/:slug" element={<ProductDetail />} />
            <Route path="/collections" element={<Collections />} />
            <Route path="/collections/:brand" element={<CollectionBrand />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/bookings" element={<Bookings />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
            <Route path="/wishlist" element={<ProtectedRoute><Wishlist /></ProtectedRoute>} />
            <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                <Route index element={<AdminDashboard />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="contacts" element={<AdminContacts />} />
                <Route path="bookings" element={<AdminBookings />} />
                <Route path="build-club" element={<AdminBuildClub />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="blog" element={<AdminBlog />} />
            </Route>
            <Route path="*" element={<NotFound />} />
        </Routes>
    );
}

function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();
    if (loading) return <FullscreenSpinner />;
    if (!user) return <Navigate to="/auth" replace />;
    return children;
}

function AdminRoute({ children }) {
    const { user, loading } = useAuth();
    if (loading) return <FullscreenSpinner />;
    if (!user) return <Navigate to="/auth?next=/admin" replace />;
    if (user.role !== "admin") return <Navigate to="/" replace />;
    return children;
}

function FullscreenSpinner() {
    return (
        <div className="flex min-h-screen items-center justify-center bg-[hsl(var(--dust-50))]" data-testid="fullscreen-spinner">
            <div className="font-display text-4xl uppercase italic tracking-wider text-[hsl(var(--ink))]">Loading…</div>
        </div>
    );
}

function NotFound() {
    return (
        <div className="flex min-h-screen items-center justify-center bg-[hsl(var(--dust-50))] px-4">
            <div className="max-w-md text-center">
                <div className="font-display text-8xl uppercase italic">404</div>
                <p className="mt-4 font-heading uppercase tracking-wider">Trail's end. This road doesn't exist.</p>
                <a href="/" className="mt-6 inline-block btn-primary" data-testid="notfound-home-link">Back to base camp →</a>
            </div>
        </div>
    );
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <AppRouter />
                <Toaster position="top-right" theme="light" />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
